import { useCallback, useMemo } from 'react'

import { useMutation } from '@apollo/client'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { useIsMuted, useLocalParticipant } from '@livekit/components-react'
import {
  MeetingsAddAudioChunkDocument,
  MeetingsAddAudioChunkMutation,
} from 'generated/resources/graphql'
import { Track } from 'livekit-client'

import { useLocalAudioRecording } from 'modules/meetings/lib/hooks/useLocalAudioRecording'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'

interface UseTranscriptOptions {
  maxChunkDuration?: number
  minChunkDuration?: number
  minSilenceDuration?: number
  silenceThresholdLow?: number
  silenceThresholdHigh?: number
}

interface UseTranscriptReturn {
  isConnected: boolean
  isRecording: boolean
  participantIdentity: string | null
  connectionError: Error | null
  isMuted: boolean
  isAddingAudioChunk: boolean
}

export const useTranscript = (options: UseTranscriptOptions = {}): UseTranscriptReturn => {
  const {
    maxChunkDuration = 20,
    minChunkDuration = 10,
    minSilenceDuration = 0.3,
    silenceThresholdLow = 0.02,
    silenceThresholdHigh = 0.04,
  } = options

  const { room, isTranscriptEnabled } = useRoom()
  const { localParticipant } = useLocalParticipant()
  const { meetingId } = useMeeting()

  const [addAudioChunk, { loading: isAddingAudioChunk }] =
    useMutation<MeetingsAddAudioChunkMutation>(MeetingsAddAudioChunkDocument)

  // Extract room data
  const roomName = room?.roomName
  const transcriptionSessionId = room?.transcriptionSession?.id
  const attendeeId = room?.transcriptionSession?.attendee.id

  // Set up microphone track reference
  const micTrack = localParticipant?.getTrackPublication(Track.Source.Microphone)
  const micRef: TrackReferenceOrPlaceholder = useMemo(() => {
    return {
      participant: localParticipant,
      source: Track.Source.Microphone,
      publication: micTrack,
    }
  }, [localParticipant, micTrack])

  const isMuted = useIsMuted(micRef)
  const isConnected = isTranscriptEnabled
  const connectionError = null

  // Handle audio chunks
  const handleAudioChunk = useCallback(
    async (blob: Blob) => {
      if (!isConnected || !meetingId || !roomName || !isTranscriptEnabled || isMuted) {
        return
      }

      // Convert blob to base64 for transmission
      const reader = new FileReader()
      reader.onloadend = async () => {
        const base64data = reader.result?.toString().split(',')[1]
        const timestampSec = Math.floor(Date.now() / 1000)
        if (base64data) {
          await addAudioChunk({
            variables: {
              attendeeId: attendeeId,
              audioChunk: base64data,
              sessionId: transcriptionSessionId,
              timestamp: timestampSec,
            },
          })
        }
      }
      reader.readAsDataURL(blob)
    },
    [
      isConnected,
      meetingId,
      roomName,
      isTranscriptEnabled,
      addAudioChunk,
      attendeeId,
      transcriptionSessionId,
      isMuted,
    ]
  )

  // Use the audio recording hook
  const { isRecording, participantIdentity } = useLocalAudioRecording({
    onAudioChunk: handleAudioChunk,
    maxChunkDuration,
    minChunkDuration,
    minSilenceDuration,
    silenceThresholdLow,
    silenceThresholdHigh,
  })

  return {
    isConnected,
    isRecording,
    participantIdentity,
    connectionError,
    isMuted,
    isAddingAudioChunk,
  }
}
